/**
 * QuizModule - Crypto Wallet Buyer Quiz
 * 
 * Features:
 * - Persistent trust header with 4.9 stars + "762,000+ Users Taking Control"
 * - Animated testimonial carousel (crypto wallet users)
 * - Unified loading screen with email form
 * - Psychological belief checkpoint screens
 * - Improved loading animation
 * - Answer-based profile tags for personalized recommendations
 */

import { useState, useEffect, useRef } from 'react';
import { 
  ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertTriangle, 
  Shield, Lock, Smartphone, Laptop, TrendingUp, Star, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatUserCount } from '@shared/userCount';

// ============================================
// TESTIMONIALS DATA (crypto wallet users)
// ============================================
interface Testimonial {
  name: string;
  text: string;
  tags: string[]; // For filtering based on user answers
}

const testimonials: Testimonial[] = [
  { 
    name: "Alex M.", 
    text: "Finally, a wallet that doesn't make me feel like I need a PhD in cryptography. Setup took 5 minutes and I feel way more secure.",
    tags: ['beginner', 'easy-to-use', 'security']
  },
  { 
    name: "Sarah K.", 
    text: "Been in crypto since 2017. Tried every wallet out there. Ryder One is the first that combines security with actual usability.",
    tags: ['experienced', 'security', 'multi-chain']
  },
  { 
    name: "Michael P.", 
    text: "Lost access to my old wallet and $12K in ETH. Never again. The recovery tag gives me peace of mind I didn't know I needed.",
    tags: ['security', 'recovery', 'ethereum']
  },
  { 
    name: "Jennifer L.", 
    text: "My exchange got hacked last year. Moving everything to self-custody was the best decision. Ryder made it painless.",
    tags: ['security', 'self-custody', 'exchange-user']
  },
  { 
    name: "David T.", 
    text: "Managing 8 different chains used to be a nightmare. Now it's all in one place. Game changer for my DeFi portfolio.",
    tags: ['multi-chain', 'defi', 'experienced']
  },
  { 
    name: "Emma R.", 
    text: "I was terrified of hardware wallets. Ryder's interface is so intuitive, my mom could use it. And the security is top-tier.",
    tags: ['beginner', 'easy-to-use', 'security']
  },
  { 
    name: "James W.", 
    text: "Three months in, zero issues. The wireless charging is clutch. Battery lasts forever. Build quality is solid.",
    tags: ['quality', 'convenience']
  },
  { 
    name: "Lisa H.", 
    text: "After the FTX collapse, I knew I had to take control. Ryder made self-custody accessible for someone like me who's not super technical.",
    tags: ['beginner', 'self-custody', 'security']
  },
  { 
    name: "Robert C.", 
    text: "The multi-sig support is exactly what I needed for my business. Finally a hardware wallet that scales with serious use.",
    tags: ['experienced', 'security', 'business']
  },
  { 
    name: "Nina S.", 
    text: "Love that it's open source. Audited the firmware myself. This is the real deal for anyone serious about security.",
    tags: ['experienced', 'security', 'open-source']
  },
];

// ============================================
// ANSWER-BASED TAG LABELS (for results page)
// ============================================
interface TagLabel {
  tags: string[]; // Quiz tags that trigger this label
  label: string;  // Human-readable label for results
  priority: number; // Higher = shown first (max 4 shown)
}

const tagLabels: TagLabel[] = [
  // Experience level (highest priority)
  { tags: ['beginner'], label: 'Perfect for Beginners', priority: 100 },
  { tags: ['intermediate'], label: 'Ideal for Growing Portfolios', priority: 99 },
  { tags: ['experienced'], label: 'Built for Power Users', priority: 98 },
  
  // Security focus (high priority)
  { tags: ['security-focused'], label: 'Maximum Security', priority: 95 },
  { tags: ['self-custody'], label: 'True Self-Custody', priority: 94 },
  { tags: ['recovery'], label: 'Bulletproof Recovery', priority: 93 },
  
  // Use case tags
  { tags: ['multi-chain'], label: 'Multi-Chain Support', priority: 92 },
  { tags: ['defi'], label: 'DeFi Ready', priority: 91 },
  { tags: ['trading'], label: 'Trading Optimized', priority: 90 },
  { tags: ['hodl'], label: 'Long-Term Storage', priority: 89 },
  
  // Device preferences
  { tags: ['mobile-first'], label: 'Mobile Friendly', priority: 87 },
  { tags: ['desktop-user'], label: 'Desktop Compatible', priority: 86 },
  
  // Specific needs
  { tags: ['exchange-user'], label: 'Exchange Alternative', priority: 85 },
  { tags: ['business'], label: 'Business Grade', priority: 84 },
  { tags: ['open-source'], label: 'Open Source Verified', priority: 83 },
];

// ============================================
// QUIZ QUESTIONS
// ============================================
interface QuizQuestion {
  id: string;
  question: string;
  subtitle?: string;
  icon?: any;
  multiSelect?: boolean;
  options: {
    text: string;
    tags: string[];
    points: number;
    icon?: any;
  }[];
}

const questions: QuizQuestion[] = [
  {
    id: 'experience',
    question: "What's your crypto experience level?",
    subtitle: "Help us personalize your recommendations",
    icon: Star,
    options: [
      { text: "Just getting started", tags: ['beginner', 'easy-to-use'], points: 10, icon: Smartphone },
      { text: "I know the basics", tags: ['intermediate'], points: 15, icon: Wallet },
      { text: "I'm a crypto veteran", tags: ['experienced', 'multi-chain'], points: 20, icon: TrendingUp },
    ]
  },
  {
    id: 'current-storage',
    question: "Where do you currently store your crypto?",
    subtitle: "Understanding your current setup helps us recommend the best solution",
    icon: Lock,
    options: [
      { text: "On an exchange (Coinbase, Binance, etc.)", tags: ['exchange-user', 'self-custody'], points: 25, icon: TrendingUp },
      { text: "Software wallet (MetaMask, Trust Wallet)", tags: ['security-focused', 'upgrade'], points: 20, icon: Smartphone },
      { text: "Another hardware wallet", tags: ['experienced', 'security-focused'], points: 15, icon: Shield },
      { text: "I don't have any crypto yet", tags: ['beginner'], points: 10, icon: Wallet },
    ]
  },
  {
    id: 'portfolio-size',
    question: "How much crypto do you hold (or plan to hold)?",
    subtitle: "This helps us understand your security needs",
    icon: Wallet,
    options: [
      { text: "Under $1,000", tags: ['beginner', 'starter'], points: 10 },
      { text: "$1,000 - $10,000", tags: ['intermediate', 'security-focused'], points: 20 },
      { text: "$10,000 - $50,000", tags: ['experienced', 'security-focused'], points: 30 },
      { text: "Over $50,000", tags: ['experienced', 'security-focused', 'high-value'], points: 40 },
    ]
  },
  {
    id: 'chains',
    question: "Which blockchains do you use?",
    subtitle: "Select all that apply",
    icon: Shield,
    options: [
      { text: "Bitcoin only", tags: ['bitcoin', 'hodl'], points: 15 },
      { text: "Ethereum & ERC-20 tokens", tags: ['ethereum', 'defi'], points: 20 },
      { text: "Multiple chains (Solana, Polygon, etc.)", tags: ['multi-chain', 'experienced'], points: 25 },
      { text: "Not sure yet / Just starting", tags: ['beginner'], points: 10 },
    ]
  },
  {
    id: 'use-case',
    question: "What do you mainly use crypto for?",
    subtitle: "This helps us tailor your experience",
    icon: TrendingUp,
    options: [
      { text: "Long-term holding (HODL)", tags: ['hodl', 'security-focused'], points: 20 },
      { text: "Active trading", tags: ['trading', 'mobile-first'], points: 25 },
      { text: "DeFi (staking, lending, yield farming)", tags: ['defi', 'multi-chain', 'experienced'], points: 30 },
      { text: "NFTs and collectibles", tags: ['nft', 'ethereum'], points: 20 },
      { text: "All of the above", tags: ['multi-chain', 'experienced'], points: 35 },
    ]
  },
  {
    id: 'security-concern',
    question: "What's your biggest security concern?",
    subtitle: "Select all that apply",
    icon: Shield,
    multiSelect: true,
    options: [
      { text: "Losing my private keys", tags: ['recovery', 'security-focused'], points: 25 },
      { text: "Exchange hacks", tags: ['self-custody', 'exchange-user'], points: 30 },
      { text: "Phishing and scams", tags: ['security-focused', 'beginner'], points: 20 },
      { text: "Not being able to access my funds when I need them", tags: ['convenience', 'mobile-first'], points: 15 },
    ]
  },
  {
    id: 'device-preference',
    question: "How do you prefer to manage your crypto?",
    subtitle: "Ryder works seamlessly with both",
    icon: Smartphone,
    options: [
      { text: "Primarily on mobile", tags: ['mobile-first'], points: 15, icon: Smartphone },
      { text: "Primarily on desktop", tags: ['desktop-user'], points: 15, icon: Laptop },
      { text: "Both equally", tags: ['mobile-first', 'desktop-user'], points: 20 },
    ]
  },
];

// ============================================
// PROFILE DEFINITIONS
// ============================================
interface Profile {
  name: string;
  slug: string;
  description: string;
  minScore: number;
  maxScore: number;
  tags: string[];
}

const profiles: Profile[] = [
  {
    name: 'Crypto Beginner',
    slug: 'beginner',
    description: "You're new to crypto and need a wallet that's secure but easy to use. Ryder One is perfect for you.",
    minScore: 0,
    maxScore: 100,
    tags: ['beginner', 'easy-to-use']
  },
  {
    name: 'Growing Investor',
    slug: 'growing-investor',
    description: "Your portfolio is growing and you need better security. Ryder One gives you peace of mind.",
    minScore: 101,
    maxScore: 150,
    tags: ['intermediate', 'security-focused']
  },
  {
    name: 'DeFi Power User',
    slug: 'defi-user',
    description: "You're active in DeFi and need multi-chain support. Ryder One handles it all.",
    minScore: 151,
    maxScore: 200,
    tags: ['experienced', 'defi', 'multi-chain']
  },
  {
    name: 'Security-First HODLer',
    slug: 'hodler',
    description: "You're holding for the long term and security is everything. Ryder One is built for you.",
    minScore: 0,
    maxScore: 250,
    tags: ['hodl', 'security-focused', 'recovery']
  },
];

// ============================================
// MAIN QUIZ COMPONENT
// ============================================
interface QuizModuleProps {
  onComplete: (profile: Profile, score: number) => void;
}

export default function QuizModule({ onComplete }: QuizModuleProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [multiSelectAnswers, setMultiSelectAnswers] = useState<string[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [email, setEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  
  const carouselRef = useRef<HTMLDivElement>(null);

  // Filter testimonials based on user tags
  const relevantTestimonials = testimonials.filter(t => 
    t.tags.some(tag => userTags.includes(tag))
  );
  const displayTestimonials = relevantTestimonials.length > 0 ? relevantTestimonials : testimonials;

  // Auto-scroll testimonials
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const scroll = () => {
      if (carousel.scrollLeft >= carousel.scrollWidth / 2) {
        carousel.scrollLeft = 0;
      } else {
        carousel.scrollLeft += 1;
      }
    };

    const interval = setInterval(scroll, 30);
    return () => clearInterval(interval);
  }, []);

  const handleAnswer = (questionId: string, option: any) => {
    const question = questions[currentQuestion];
    
    if (question.multiSelect) {
      // Multi-select logic: toggle option in array
      const optionText = option.text;
      const isSelected = multiSelectAnswers.includes(optionText);
      
      if (isSelected) {
        // Remove option
        setMultiSelectAnswers(multiSelectAnswers.filter(a => a !== optionText));
      } else {
        // Add option
        setMultiSelectAnswers([...multiSelectAnswers, optionText]);
      }
      return; // Don't auto-advance for multi-select
    }
    
    // Single-select logic (original)
    const newAnswers = { ...answers, [questionId]: option };
    setAnswers(newAnswers);
    
    // Update tags and score
    const newTags = [...userTags, ...option.tags];
    setUserTags(newTags);
    setScore(score + option.points);

    // Move to next question or show loading
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      // Quiz complete - show email form
      setShowEmailForm(true);
    }
  };
  
  const handleMultiSelectContinue = () => {
    const question = questions[currentQuestion];
    
    if (multiSelectAnswers.length === 0) {
      toast.error('Please select at least one option');
      return;
    }
    
    // Calculate combined tags and points from all selected options
    let combinedTags: string[] = [];
    let combinedPoints = 0;
    
    multiSelectAnswers.forEach(answerText => {
      const option = question.options.find(opt => opt.text === answerText);
      if (option) {
        combinedTags = [...combinedTags, ...option.tags];
        combinedPoints += option.points;
      }
    });
    
    // Update state
    setAnswers({ ...answers, [question.id]: multiSelectAnswers });
    setUserTags([...userTags, ...combinedTags]);
    setScore(score + combinedPoints);
    setMultiSelectAnswers([]); // Reset for next multi-select question
    
    // Move to next question or complete
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setShowEmailForm(true);
    }
  };

  const handleSubmitEmail = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setShowEmailForm(false);

    // Simulate loading with stops
    const stops = [50, 75, 85, 95, 97, 100];
    let currentStop = 0;

    const loadingInterval = setInterval(() => {
      if (currentStop < stops.length) {
        setLoadingProgress(stops[currentStop]);
        currentStop++;
      } else {
        clearInterval(loadingInterval);
        
        // Determine profile based on score and tags
        let profile = profiles[0]; // Default
        
        if (userTags.includes('defi') && userTags.includes('experienced')) {
          profile = profiles[2]; // DeFi Power User
        } else if (userTags.includes('hodl') && userTags.includes('security-focused')) {
          profile = profiles[3]; // Security-First HODLer
        } else if (score > 100) {
          profile = profiles[1]; // Growing Investor
        }
        
        setSelectedProfile(profile);
        setIsComplete(true);
        
        // Complete quiz after short delay
        setTimeout(() => {
          onComplete(profile, score);
        }, 1500);
      }
    }, 600);
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Trust header component
  const TrustHeader = () => (
    <div className="flex flex-col items-center gap-2 mb-6 pb-4 border-b">
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
        <span className="text-sm font-medium ml-2">4.9/5</span>
      </div>
      <p className="text-sm text-muted-foreground">{formatUserCount()} Users Taking Control</p>
    </div>
  );

  // Loading screen
  if (isLoading || isComplete) {
    return (
      <div className="min-h-[500px] flex flex-col items-center justify-center p-6">
        <TrustHeader />
        
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
            <h3 className="text-xl font-semibold">
              {isComplete ? 'Your Results Are Ready!' : 'Analyzing Your Needs...'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isComplete 
                ? `Perfect match: ${selectedProfile?.name}` 
                : 'Finding the perfect wallet setup for you'}
            </p>
          </div>

          <div className="w-full bg-secondary rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>

          {isComplete && (
            <div className="text-center space-y-2 animate-in fade-in duration-500">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
              <p className="text-sm font-medium">Redirecting to your personalized results...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Email form
  if (showEmailForm) {
    return (
      <div className="min-h-[500px] flex flex-col p-6">
        <TrustHeader />
        
        <div className="flex-1 flex flex-col items-center justify-center space-y-6">
          <div className="text-center space-y-2">
            <Shield className="w-16 h-16 text-primary mx-auto" />
            <h3 className="text-2xl font-bold">Almost There!</h3>
            <p className="text-muted-foreground max-w-md">
              Enter your email to see your personalized wallet recommendation and get exclusive early access pricing.
            </p>
          </div>

          <div className="w-full max-w-md space-y-4">
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmitEmail()}
              className="h-12 text-center"
            />
            <Button 
              onClick={handleSubmitEmail}
              className="w-full h-12 text-base font-semibold"
              style={{ backgroundColor: '#6a63f5' }}
            >
              See My Results →
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              We respect your privacy. No spam, ever.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Quiz questions
  const question = questions[currentQuestion];
  const Icon = question.icon;

  return (
    <div className="min-h-[500px] flex flex-col">
      <TrustHeader />

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Question {currentQuestion + 1} of {questions.length}</span>
          <span className="font-medium">{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
        </div>
        <div className="w-full bg-secondary rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 space-y-6">
        <div className="text-center space-y-2">
          {Icon && <Icon className="w-12 h-12 text-primary mx-auto" />}
          <h3 className="text-2xl font-bold">{question.question}</h3>
          {question.subtitle && (
            <p className="text-sm text-muted-foreground">{question.subtitle}</p>
          )}
        </div>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, idx) => {
            const OptionIcon = option.icon;
            const isSelected = question.multiSelect && multiSelectAnswers.includes(option.text);
            return (
              <button
                key={idx}
                onClick={() => handleAnswer(question.id, option)}
                className={`w-full p-4 border-2 rounded-lg transition-all text-left group ${
                  isSelected 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border hover:border-primary hover:bg-primary/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  {OptionIcon && <OptionIcon className="w-5 h-5 text-muted-foreground group-hover:text-primary" />}
                  <span className="font-medium">{option.text}</span>
                  {question.multiSelect ? (
                    <div className={`w-5 h-5 ml-auto rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                    }`}>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                  ) : (
                    <ChevronRight className="w-5 h-5 ml-auto text-muted-foreground group-hover:text-primary" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Continue button for multi-select questions */}
        {question.multiSelect && multiSelectAnswers.length > 0 && (
          <Button
            onClick={handleMultiSelectContinue}
            className="w-full mt-4"
            style={{ backgroundColor: '#6a63f5' }}
          >
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Back button */}
      {currentQuestion > 0 && (
        <button
          onClick={goBack}
          className="mt-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous Question
        </button>
      )}

      {/* Testimonial carousel removed per user request */}
    </div>
  );
}
